import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import attendanceAPI from '../api/attendanceAPI';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import universityLogo from '../../assets/images/Benadir University Logo.png';
import { useAttendanceAuth } from '../context/AttendanceAuthContext';
import SharedClassBadge from './SharedClassBadge';
import { 
  MdArrowBack, 
  MdEdit, 
  MdSchedule,
  MdSchool,
  MdPeople,
  MdCalendarToday,
  MdDownload,
  MdSubject,
  MdLocationOn
} from 'react-icons/md';

/**
 * View Timetable Details Component
 * Displays detailed timetable information
 */
function ViewTimetable() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getToken, user } = useAttendanceAuth();
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [showSessionDetails, setShowSessionDetails] = useState(false);

  // Helper function to fix academic year display (replace PG with UG)
  const formatAcademicYear = (academicYear) => {
    if (!academicYear) return '';
    // Replace "PG" with "UG" in the academic year string
    return academicYear.replace(/\s*-\s*PG\s*$/i, ' - UG').replace(/\s*PG\s*$/i, ' UG');
  };

  useEffect(() => {
    // Validate timetable ID first
    if (!id || id === 'undefined') {
      setError('Invalid timetable ID');
      setLoading(false);
      return;
    }
    
    const timetableId = parseInt(id);
    if (isNaN(timetableId) || timetableId <= 0) {
      setError('Invalid timetable ID format');
      setLoading(false);
      return;
    }
    
    fetchTimetable();
  }, [id]);

  const fetchTimetable = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Additional validation before API call
      if (!id || id === 'undefined') {
        setError('Timetable ID is required');
        return;
      }
      
      const timetableId = parseInt(id);
      if (isNaN(timetableId) || timetableId <= 0) {
        setError('Invalid timetable ID format');
        return;
      }
      
      const token = getToken();
      const result = await attendanceAPI.getTimetable(timetableId, token);
      
      if (result.success) {
        setTimetable(result.timetable);
      } else {
        setError(result.message || 'Failed to load timetable');
      }
    } catch (err) {
      console.error('Error fetching timetable:', err);
      setError(err.message || 'Failed to load timetable');
    } finally {
      setLoading(false);
    }
  };

  const getStateBadge = (state) => {
    const stateLower = state?.toLowerCase() || 'draft';
    switch (stateLower) {
      case 'active':
        return (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
            Active
          </span>
        );
      case 'ended':
        return (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
            Ended
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
            Draft
          </span>
        );
    }
  };

  const weekdays = [
    { id: 1, short: 'SAT', names: ['sat', 'saturday'] },
    { id: 2, short: 'SUN', names: ['sun', 'sunday'] },
    { id: 3, short: 'MON', names: ['mon', 'monday'] },
    { id: 4, short: 'TUE', names: ['tue', 'tues', 'tuesday'] },
    { id: 5, short: 'WED', names: ['wed', 'wednesday'] },
    { id: 6, short: 'THU', names: ['thu', 'thur', 'thurs', 'thursday'] },
    { id: 7, short: 'FRI', names: ['fri', 'friday'] },
  ];

  const isSubjectScheduled = (subject, periodIndex, weekday) => {
    const schedule = subject?.schedule || {};
    const periodNumber = periodIndex + 1;
    const ids = schedule[`period_${periodNumber}_ids`];
    if (Array.isArray(ids) && ids.length > 0) {
      return ids.map(Number).includes(weekday.id);
    }

    const names = schedule[`period_${periodNumber}`];
    return Array.isArray(names) && names.some(day =>
      weekday.names.includes(String(day).trim().toLowerCase())
    );
  };
  const addMinutesToDisplayTime = (time, minutesToAdd) => {
    const match = String(time || '').trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return '';

    let hours = Number(match[1]) % 12;
    const minutes = Number(match[2]);
    if (match[3].toUpperCase() === 'PM') hours += 12;

    const totalMinutes = (hours * 60 + minutes + minutesToAdd) % (24 * 60);
    const outputHours = Math.floor(totalMinutes / 60);
    const suffix = outputHours >= 12 ? 'PM' : 'AM';
    return `${outputHours % 12 || 12}:${String(totalMinutes % 60).padStart(2, '0')} ${suffix}`;
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading timetable...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center space-x-2 mb-2">
          <h3 className="text-red-800 font-semibold">Error</h3>
        </div>
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={() => navigate('/attendance/faculty-admin/timetables')}
          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition"
        >
          Back to Timetables
        </button>
      </div>
    );
  }

  if (!timetable) {
    return null;
  }
  const periods = timetable.periods || [];
  const visibleWeekdays = weekdays.filter(weekday =>
    periods.some((period, periodIndex) =>
      timetable.subjects?.some(subject => isSubjectScheduled(subject, periodIndex, weekday))
    )
  );
  const breakStart = periods[1]?.end_time || '';
  const scheduleColumns = periods.flatMap((period, periodIndex) => {
    const periodColumn = { type: 'period', period, periodIndex };
    if (periodIndex === 2) {
      return [{
        type: 'break',
        id: 'break-after-period-2',
        startTime: breakStart,
        endTime: addMinutesToDisplayTime(breakStart, 30),
      }, periodColumn];
    }
    return [periodColumn];
  });
  const parseLocalDate = (value) => {
    const [year, month, day] = String(value || '').split('-').map(Number);
    return year && month && day ? new Date(year, month - 1, day) : null;
  };
  const dateKey = (date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
  const formatPortalDate = (value) => {
    const date = parseLocalDate(value);
    return date ? date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  };
  const getTeachingWeekKey = (date) => {
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - ((date.getDay() + 1) % 7)); // Saturday-Friday teaching week
    return dateKey(weekStart);
  };

  const exceptionDates = Array.isArray(timetable.exception_dates) ? timetable.exception_dates : [];
  const midtermException = exceptionDates.find(exception => exception.is_midterm)
    || exceptionDates.find(exception => /mid\s*-?\s*term/i.test(`${exception.name || ''} ${exception.reason || ''}`));
  const midtermStart = parseLocalDate(midtermException?.start_date);
  const midtermEnd = parseLocalDate(midtermException?.end_date);
  const exceptionRanges = exceptionDates.map(exception => ({
    ...exception,
    start: parseLocalDate(exception.start_date),
    end: parseLocalDate(exception.end_date),
  })).filter(exception => exception.start && exception.end);
  const isExceptionDate = (date) => exceptionRanges.some(exception =>
    date >= exception.start && date <= exception.end
  );
  const jsDayToWeekdayId = { 6: 1, 0: 2, 1: 3, 2: 4, 3: 5, 4: 6, 5: 7 };
  const termStart = parseLocalDate(timetable.start_date);
  const termEnd = parseLocalDate(timetable.end_date);
  const sessionCalculations = (timetable.subjects || []).map(subject => {
    const slotsByWeekday = {};
    weekdays.forEach(weekday => {
      slotsByWeekday[weekday.id] = periods.reduce((count, period, periodIndex) =>
        count + (isSubjectScheduled(subject, periodIndex, weekday) ? 1 : 0), 0);
    });
    const weeklySessions = Object.values(slotsByWeekday).reduce((sum, count) => sum + count, 0);
    let beforeSessions = 0;
    let afterSessions = 0;
    const beforeWeeks = new Set();
    const afterWeeks = new Set();

    if (termStart && termEnd) {
      for (let cursor = new Date(termStart); cursor <= termEnd; cursor.setDate(cursor.getDate() + 1)) {
        const slots = slotsByWeekday[jsDayToWeekdayId[cursor.getDay()]] || 0;
        if (!slots || isExceptionDate(cursor)) continue;
        const weekKey = getTeachingWeekKey(cursor);
        if (midtermStart && cursor < midtermStart) {
          beforeSessions += slots;
          beforeWeeks.add(weekKey);
        } else if (midtermEnd && cursor > midtermEnd) {
          afterSessions += slots;
          afterWeeks.add(weekKey);
        } else if (!midtermStart || !midtermEnd) {
          beforeSessions += slots;
          beforeWeeks.add(weekKey);
        }
      }
    }

    const beforeWeekCount = beforeWeeks.size;
    const afterWeekCount = afterWeeks.size;
    const teachingWeekCount = new Set([...beforeWeeks, ...afterWeeks]).size;

    return {
      id: subject.id,
      name: subject.subject_name || subject.name || 'Subject',
      weeklySessions,
      beforeWeeks: beforeWeekCount,
      beforeSessions,
      afterWeeks: afterWeekCount,
      afterSessions,
      teachingWeeks: teachingWeekCount,
      totalSessions: beforeSessions + afterSessions,
    };
  });
  const formatTermForTitle = (termName) => {
    const value = String(termName || '').trim();
    const romanMatch = value.match(/semester\s+(i{1,3}|iv|v|vi{0,3})$/i);
    const numberMatch = value.match(/semester\s+(\d+)$/i);
    const romanNumbers = { I: 1, II: 2, III: 3, IV: 4, V: 5, VI: 6, VII: 7, VIII: 8 };
    const ordinalWords = ['First', 'Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth'];
    const number = romanMatch ? romanNumbers[romanMatch[1].toUpperCase()] : Number(numberMatch?.[1]);
    return number && ordinalWords[number - 1] ? `${ordinalWords[number - 1]} Semester` : value;
  };

  const handleDownloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const token = getToken();
      const termResult = await attendanceAPI.getActiveTermConfig(token);
      if (!termResult?.success) {
        throw new Error(termResult?.message || 'Unable to load the current Timetable Term Configuration');
      }

      const rawFacultyName = user?.faculty?.name || user?.faculty_name || 'Faculty';
      const facultyHeading = /^faculty\s+of\s+/i.test(rawFacultyName)
        ? rawFacultyName.toUpperCase()
        : `FACULTY OF ${rawFacultyName.toUpperCase()}`;
      const academicYear = String(termResult.academic_year_name || timetable.academic_year || '')
        .replace(/\s*-?\s*(UG|PG)\s*$/i, '')
        .replace(/\s*[-–]\s*/g, ' - ');
      const termTitle = formatTermForTitle(termResult.academic_term_name);
      const batchNames = timetable.batch_names || [];
      const batchTitle = batchNames.join(', ') || timetable.name || 'Timetable';
      const referenceBatchTitle = (batchNames.length ? batchNames : [timetable.name])
        .filter(Boolean)
        .map(name => {
          const match = String(name).match(/(\d{2})\D*$/);
          return match ? match[1] : String(name);
        })
        .join(', ');
      const formatPdfTime = (time) => String(time || '').replace(/\s+/g, '');

      const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4', compress: true });
      const pageWidth = doc.internal.pageSize.getWidth();
      const logo = new Image();
      logo.src = universityLogo;
      await new Promise((resolve, reject) => {
        logo.onload = resolve;
        logo.onerror = () => reject(new Error('Unable to load the university logo'));
      });
      doc.addImage(logo, 'PNG', (pageWidth - 64) / 2, 34, 64, 64, 'university-logo', 'FAST');

      doc.setTextColor(0, 0, 0);
      doc.setFont('times', 'bold');
      doc.setFontSize(17);
      doc.text(facultyHeading, pageWidth / 2, 124, { align: 'center' });
      const subtitle = `Time Table of ${termTitle} in Academic Year ${academicYear}`;
      doc.setFontSize(12.5);
      doc.text(subtitle, pageWidth / 2, 151, { align: 'center' });
      const subtitleWidth = doc.getTextWidth(subtitle);
      doc.setLineWidth(0.7);
      doc.line((pageWidth - subtitleWidth) / 2, 154, (pageWidth + subtitleWidth) / 2, 154);

      const timeHeaders = scheduleColumns.map(column =>
        column.type === 'break'
          ? `${formatPdfTime(column.startTime)}-${formatPdfTime(column.endTime)}`
          : `${formatPdfTime(column.period.start_time)}-${formatPdfTime(column.period.end_time)}`
      );
      const head = [
        [{
          content: `BATCH ${referenceBatchTitle}`,
          colSpan: scheduleColumns.length + 1,
          styles: { fontSize: 13, fontStyle: 'bold', minCellHeight: 30 },
        }],
        ['Days', ...timeHeaders],
      ];
      const body = visibleWeekdays.map(weekday => [
        weekday.short === 'TUE' ? 'TUES' : weekday.short,
        ...scheduleColumns.map(column => {
          if (column.type === 'break') return 'Breakfast';
          const scheduledSubjects = timetable.subjects.filter(subject =>
            isSubjectScheduled(subject, column.periodIndex, weekday)
          );
          return scheduledSubjects.map(subject => {
            const subjectName = subject.subject_name || subject.name || '';
            const tutorName = subject.tutor_name || subject.tutor || 'Tutor not assigned';
            return `${subjectName}\n(${tutorName})`;
          }).join('\n\n');
        }),
      ]);

      autoTable(doc, {
        head,
        body,
        startY: 194,
        theme: 'grid',
        styles: {
          font: 'times',
          fontSize: 10,
          textColor: [0, 0, 0],
          fillColor: [255, 255, 255],
          lineColor: [0, 0, 0],
          lineWidth: 0.45,
          halign: 'center',
          valign: 'middle',
          cellPadding: 3,
          minCellHeight: 38,
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
          font: 'times',
          fontStyle: 'bold',
          lineColor: [0, 0, 0],
          lineWidth: 0.55,
          minCellHeight: 28,
        },
        columnStyles: { 0: { cellWidth: 42 } },
        margin: { left: 22, right: 22 },
        tableWidth: pageWidth - 44,
      });

      const safeName = batchTitle.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
      doc.save(`${facultyHeading.replace(/[^a-z0-9]+/gi, '-')}-${termTitle.replace(/\s+/g, '-')}-${academicYear.replace(/\s+/g, '')}-${safeName}.pdf`);
    } catch (downloadError) {
      console.error('Error generating timetable PDF:', downloadError);
      window.alert(downloadError.message || 'Failed to download timetable PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/attendance/faculty-admin/timetables')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-4 transition"
        >
          <MdArrowBack />
          <span>Back to Timetables</span>
        </button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              {timetable.name || 'Timetable Details'}
            </h1>
            {getStateBadge(timetable.state)}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60 transition flex items-center space-x-2"
            >
              <MdDownload />
              <span>{downloadingPdf ? 'Preparing PDF...' : 'Download Timetable'}</span>
            </button>
            {(timetable.state === 'draft' || timetable.state === 'active') && (
              <button
                onClick={() => navigate(`/attendance/faculty-admin/timetable/${id}/edit`)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition flex items-center space-x-2"
              >
                <MdEdit />
                <span>Edit Timetable</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Timetable Info Card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Timetable Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(timetable.campus_name || timetable.campus) && (
            <div className="flex items-center space-x-3">
              <MdLocationOn className="text-blue-500 text-xl" />
              <div>
                <p className="text-sm text-gray-500">Campus</p>
                <p className="font-semibold text-gray-800">{timetable.campus_name || timetable.campus}</p>
              </div>
            </div>
          )}
          {timetable.batches && (
            <div className="flex items-center space-x-3">
              <MdPeople className="text-blue-500 text-xl" />
              <div>
                <p className="text-sm text-gray-500">Batches</p>
                <p className="font-semibold text-gray-800">
                  {Array.isArray(timetable.batches)
                    ? timetable.batches.map(b => b.name || b).join(', ')
                    : timetable.batches}
                </p>
              </div>
            </div>
          )}

          {timetable.academic_year && (
            <div className="flex items-center space-x-3">
              <MdSchool className="text-blue-500 text-xl" />
              <div>
                <p className="text-sm text-gray-500">Academic Year</p>
                <p className="font-semibold text-gray-800">{formatAcademicYear(timetable.academic_year)}</p>
              </div>
            </div>
          )}

          {timetable.semester && (
            <div className="flex items-center space-x-3">
              <MdSchedule className="text-blue-500 text-xl" />
              <div>
                <p className="text-sm text-gray-500">Semester</p>
                <p className="font-semibold text-gray-800">{timetable.semester}</p>
              </div>
            </div>
          )}

          {timetable.start_date && timetable.end_date && (
            <div className="flex items-center space-x-3">
              <MdCalendarToday className="text-blue-500 text-xl" />
              <div>
                <p className="text-sm text-gray-500">Date Range</p>
                <p className="font-semibold text-gray-800">
                  {new Date(timetable.start_date).toLocaleDateString()} - {new Date(timetable.end_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Exception Dates */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Exception Dates</h2>
        {exceptionDates.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {exceptionDates.map(exception => (
              <div key={exception.id || `${exception.start_date}-${exception.end_date}`} className="flex items-start justify-between gap-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <div>
                  <p className="font-semibold text-gray-900">{exception.name || exception.reason || 'Exception'}</p>
                  {exception.reason && exception.reason !== exception.name && (
                    <p className="mt-1 text-sm text-gray-600">{exception.reason}</p>
                  )}
                </div>
                <p className="shrink-0 text-sm font-medium text-amber-900">
                  {formatPortalDate(exception.start_date)} - {formatPortalDate(exception.end_date)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No active exception dates apply to this timetable.</p>
        )}
      </div>

      {/* Session Calculation */}
      {sessionCalculations.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Session Calculation</h2>
              <p className="mt-1 text-xs text-gray-500">One selected period on one teaching day equals one session. Exception dates are excluded.</p>
            </div>
            <button
              type="button"
              onClick={() => setShowSessionDetails(value => !value)}
              className="rounded-md border border-blue-500 px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
            >
              {showSessionDetails ? 'Hide detailed breakdown' : 'Show detailed breakdown'}
            </button>
          </div>

          {!showSessionDetails ? (
            <table className="w-full table-fixed border-collapse text-left text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="w-[46%] border-b px-3 py-3">Subject</th>
                  <th className="border-b px-2 py-3 text-center">Weekly sessions</th>
                  <th className="border-b px-2 py-3 text-center">Teaching weeks</th>
                  <th className="border-b px-2 py-3 text-center">Total sessions</th>
                </tr>
              </thead>
              <tbody>
                {sessionCalculations.map(row => (
                  <tr key={row.id} className="border-b last:border-b-0">
                    <td className="break-words px-3 py-3 font-medium text-gray-900">{row.name}</td>
                    <td className="px-2 py-3 text-center">{row.weeklySessions}</td>
                    <td className="px-2 py-3 text-center">{row.teachingWeeks}</td>
                    <td className="px-2 py-3 text-center font-bold">{row.totalSessions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : midtermException ? (
            <table className="w-full table-fixed border-collapse text-left text-[11px] lg:text-xs">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="w-[24%] border-b px-2 py-3">Subject</th>
                  <th className="border-b px-1 py-3 text-center">Weekly<br />sessions</th>
                  <th className="border-b px-1 py-3 text-center">Before midterm<br />weeks</th>
                  <th className="border-b px-1 py-3 text-center">Before<br />sessions</th>
                  <th className="border-b px-1 py-3 text-center">After midterm<br />weeks</th>
                  <th className="border-b px-1 py-3 text-center">After<br />sessions</th>
                  <th className="border-b px-1 py-3 text-center">Total teaching<br />weeks</th>
                  <th className="border-b px-1 py-3 text-center">Total<br />sessions</th>
                </tr>
              </thead>
              <tbody>
                {sessionCalculations.map(row => (
                  <tr key={row.id} className="border-b last:border-b-0">
                    <td className="break-words px-2 py-3 font-medium text-gray-900">{row.name}</td>
                    <td className="px-1 py-3 text-center">{row.weeklySessions}</td>
                    <td className="px-1 py-3 text-center">{row.beforeWeeks}</td>
                    <td className="px-1 py-3 text-center">{row.beforeSessions}</td>
                    <td className="px-1 py-3 text-center">{row.afterWeeks}</td>
                    <td className="px-1 py-3 text-center">{row.afterSessions}</td>
                    <td className="px-1 py-3 text-center font-semibold">{row.teachingWeeks}</td>
                    <td className="px-1 py-3 text-center font-bold">{row.totalSessions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              Add an active exception named “Midterm” in Odoo to enable the before/after breakdown.
            </div>
          )}
        </div>
      )}
      {/* Weekly Schedule Matrix */}
      {timetable.subjects && timetable.subjects.length > 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Subjects Schedule</h2>
          <div className="w-full overflow-hidden">
            <table className="w-full table-fixed border-collapse border border-gray-300 text-center">
              <caption className="border border-b-0 border-gray-300 bg-gray-50 px-4 py-3 text-lg font-bold text-gray-900 caption-top">
                {(timetable.campus_name || timetable.campus) ? `${timetable.campus_name || timetable.campus} · ` : ''}
                {timetable.batch_names?.length === 1 ? 'BATCH' : 'BATCHES'}: {timetable.batch_names?.join(', ') || timetable.name}
              </caption>
              <thead>
                <tr className="bg-blue-50">
                  <th className="w-14 border border-gray-300 px-1 py-2 text-xs font-bold text-gray-800">Days</th>
                  {scheduleColumns.map(column => column.type === 'break' ? (
                    <th key={column.id} className="w-[8%] border border-amber-300 bg-amber-50 px-1 py-2 text-xs font-bold text-amber-900">
                      <div>Break</div>
                      <div className="mt-1 text-[10px] leading-tight font-semibold text-amber-700">
                        {column.startTime} - {column.endTime}
                      </div>
                    </th>
                  ) : (
                    <th key={column.period.id || column.periodIndex} className="border border-gray-300 px-1 py-2 text-xs font-bold text-gray-800">
                      <div>{column.period.name || `Period ${column.periodIndex + 1}`}</div>
                      <div className="mt-1 text-[10px] leading-tight font-semibold text-gray-600">
                        {column.period.start_time} - {column.period.end_time}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleWeekdays.map(weekday => (
                  <tr key={weekday.id} className="hover:bg-gray-50">
                    <th className="border border-gray-300 bg-gray-50 px-1 py-2 text-xs font-bold text-gray-800">
                      {weekday.short}
                    </th>
                    {scheduleColumns.map(column => {
                      if (column.type === 'break') {
                        return (
                          <td key={`${column.id}-${weekday.id}`} className="h-20 border border-amber-300 bg-amber-50 p-1 align-middle text-xs font-semibold text-amber-800">
                            Break
                          </td>
                        );
                      }

                      const { period, periodIndex } = column;
                      const scheduledSubjects = timetable.subjects.filter(subject =>
                        isSubjectScheduled(subject, periodIndex, weekday)
                      );
                      return (
                        <td key={period.id || periodIndex} className="h-20 border border-gray-300 p-1 align-middle">
                          {scheduledSubjects.length > 0 ? scheduledSubjects.map(subject => (
                            <div key={subject.id} className="py-1">
                              <div className="break-words text-xs font-semibold leading-tight text-gray-900">{subject.subject_name || subject.name}</div>
                              {subject.is_shared && <div className='mt-1'><SharedClassBadge /></div>}
                              <div className="mt-1 break-words text-[10px] leading-tight text-gray-600">
                                {subject.tutor_name || subject.tutor || 'Tutor not assigned'}
                              </div>
                            </div>
                          )) : <span className="text-gray-300">—</span>}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <MdSubject className="text-4xl text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No subjects in this timetable</p>
        </div>
      )}
    </div>
  );
}

export default ViewTimetable;
